/**
 * @file 后端API返回统计数据的相关信息
 * 字段值 key：从返回数据中取值的 path，
 * 比如：'show' 代表展示数的字段，该值一般不会变，实际取值时可能会加上嵌套路径，如'stat_data'
 */
import _ from 'lodash';
import { campaignType } from 'src/const/state/campaign';

import { isAdmin } from 'src/utils/authUtil';
import { COLUMN_WIDTH } from 'src/const/column-width';
import store from 'src/store';
import SkanNumberHover from 'src/components/dashboard/perf/skan-num-hover';
import { isDiagnosisV2White, isSearchAdsMtWhite } from 'src/const/whiteList';
import { EnumCommonData } from 'src/const/statistics/basic-performance';
import { EnumInAppEventData } from 'src/const/statistics/basic-inapp-event';
import { EnumAttributionData, EnumAttributionSkanData } from 'src/const/statistics/basic-attribute';
import { EnumConversionData } from 'src/const/statistics/basic-conversion';
import { EnumVideoPlayData, EnumUVData } from 'src/const/statistics/basic-video-play';
import { EnumPageEventData } from 'src/const/statistics/basic-web-event';
import { METRIC_TYPE } from 'src/const/statistics/metrics-emun';
import { EnumEngagementData } from 'src/const/statistics/basic-engagement';
import { EnumBasicSkanData } from 'src/const/statistics/basic-skan';
import { EnumInAppEventSkanData } from 'src/const/statistics/basic-inapp-event-skan';
import { EnumLiveData } from 'src/const/statistics/basic-live';
import { EnumAssetData } from 'src/const/statistics/basic-asset';
import { EnumOnsiteEventData } from 'src/const/statistics/basic-onsite-event';
import ViPointGray from 'src/assets/images/vi-point-gray.svg';
import ViPointBlue from 'src/assets/images/vi-point-blue.svg';
import { getStatisticDataKey, isPcmWebStatistic } from 'src/const/statistics/formatters';

// 筛选出所有的skan指标
const getAllSkanOptions = () => {
  let tempBasicSkanKey = EnumBasicSkanData.map(item => item.key);
  tempBasicSkanKey = tempBasicSkanKey.concat(EnumAttributionSkanData.map(item => item.key));
  EnumInAppEventSkanData.forEach((items) => {
    tempBasicSkanKey = tempBasicSkanKey.concat(items.children.map(childrenItems => childrenItems.key));
  });
  return tempBasicSkanKey;
};
// pageEvent增加hover
const pageEventHover = pageEventData => (pageEventData.map(childItem => ({
  ...childItem,
  type: 'component',
  component: {
    props: {
      row: { type: Object, required: true },
    },
    render(h) {
      const itemKey = getStatisticDataKey(this.row, childItem.key);
      const isPcmWeb = isPcmWebStatistic(this.row);
      // fix某些指标没有formatter
      const numberData = childItem.formatter ? childItem.formatter(this.row, {}, itemKey) : itemKey;
      const formatNumberData = typeof numberData === 'undefined' ? '-' : numberData;
      if (!isPcmWeb) {
        return (
          <div class="display-status">
            <div class="main-status">
              { formatNumberData }
            </div>
          </div>);
      }
      return (
        <SkanNumberHover
          isPcmWeb={isPcmWeb}
          tooltipTextKey='module_dc_hover_pcm'
          numberData={numberData}
        />
      );
    },
  },
}))
);
// in app event dc增加hover
const inAppEventDcHover = inAppEventdata => (inAppEventdata.map(childItem => ({
  ...childItem,
  type: 'component',
  component: {
    props: {
      row: { type: Object, required: true },
    },
    render(h) {
      const isMMPDcData = this.row.dedicate_type === campaignType.dedicatedCampaign;
      const itemKey = getStatisticDataKey(this.row, childItem.key);
      // fix某些指标没有formatter
      const numberData = childItem.formatter ? childItem.formatter(this.row, {}, itemKey) : itemKey;
      const formatNumberData = typeof numberData === 'undefined' ? '-' : numberData;
      if (!isMMPDcData) {
        return (
          <div class="display-status">
            <div class="main-status">
              { formatNumberData }
            </div>
          </div>);
      }
      return (
        <SkanNumberHover
          isMMPDcData={isMMPDcData}
          tooltipTextKey='module_dc_hover_idmatching'
          numberData={numberData}
        />
      );
    },
  },
}))
);

const skanHoverRendered = item => ({
  type: 'component',
  component: {
    props: {
      row: { type: Object, required: true },
    },
    render(h) {
      const itemKey = getStatisticDataKey(this.row, item.key);
      const isDcSkan = this.row.dedicate_type === campaignType.dedicatedCampaign;
      const numberData = item.formatter(this.row, {}, itemKey);
      const formatNumberData = typeof numberData === 'undefined' ? '-' : numberData;
      if (!isDcSkan) {
        return (
          <div class="display-status">
            <div class="main-status">
              { formatNumberData }
            </div>
          </div>);
      }
      return (
        <SkanNumberHover
          isSkanData={isDcSkan}
          numberData={numberData}
        />
      );
    },
  },
});

const getAllSkanInAppAttr = () => {
  let tempSkanInAppKey = [];
  EnumInAppEventSkanData.forEach((items) => {
    tempSkanInAppKey = tempSkanInAppKey.concat(items.children.map(childrenItems => childrenItems.key));
  });
  return tempSkanInAppKey;
};

// Attribution 里 Adgroup 层级不可见的指标列表
export const getSkanAttrUnAvailableInAdgroup = () => [
  // Skan Attribute
  'skan_active_register_show',
  'skan_active_register_show_cost',
  'skan_active_pay_show',
  'skan_active_pay_show_cost',
  'skan_active_register_click',
  'skan_active_register_click_cost',
  'skan_active_pay_click',
  'skan_active_pay_click_cost',
];

// Adgroup 层级
// 除 App Install (SKAN)和 Cost per App Install (SKAN)之外的所有指标，显示 -
export const getSkanInAppAttrUnAvailableInAdgroup = () => {
  let tempSkanKey = [];
  const validKeys = [
    'skan_event_app_install',
  ];
  EnumInAppEventSkanData.forEach((items) => {
    if (!validKeys.includes(items.key)) {
      tempSkanKey = tempSkanKey.concat(items.children.map(childrenItems => childrenItems.key));
    }
  });
  return tempSkanKey;
};

const getAllBasicAttr = () => EnumBasicSkanData.map(item => item.key);
export const allSkanOptions = getAllSkanOptions();
export const allSkanTimeAttrOptions = getAllSkanInAppAttr();
export const allSkanBasicOptions = getAllBasicAttr();
// 保留的转化未对回数据指标
export const NotTimeAttrFields = [
  'active',
  'active_cost',
  'active_rate',
  'cost_per_active',
  'active_per_install',
  'value_per_active',
  'total_active_value',
  'conversion_cost',
  'conversion_rate',
  'convert_cnt',
  'effect_cnt',
  'effect_cost',
  'effect_rate',
  'ix_page_view_count',
  'cost_per_ix_page_view_count',
  'ix_page_view_count_per_click',
  'ix_button_click_count',
  'cost_per_ix_button_click_count',
  'ix_button_click_count_per_click',
  'ix_product_click_count',
  'cost_per_ix_product_click_count',
  'ix_product_click_count_per_click',
];

// 保留的转化未对回数据指标 tea param 映射
export const NotTimeAttrFieldsTeaMap = {
  active: 'realtime_install',
  active_cost: 'realtime_install_cost',
  active_rate: 'realtime_installrate',
  conversion_cost: 'realtimecpa',
  conversion_rate: 'realtime_CVR',
  convert_cnt: 'realtime_conversion',
  effect_cnt: 'realtime_results',
  effect_cost: 'realtime_per_result',
  effect_rate: 'realtime_result_rate',
};

// custom-list Each列表
export const EnumInAppEventDataEach = EnumInAppEventData.filter(
  item => !item.onlyForUniq && !(typeof item.hide === 'function' ? item.hide() : item.hide),
).map((item, index) => {
  // real-time app install 和 app install事件的each都不能选，直接不显示
  const dummy = _.cloneDeep(item);
  dummy.children = item.children.slice(3).filter(
    child => !child.onlyForUniq && !(typeof child.hide === 'function' ? child.hide() : child.hide),
  );
  dummy.children.forEach((child, index) => {
    child.attribute = index + 1;
  });
  return dummy;
});
// custom-list Unique列表
export const EnumInAppEventDataUnique = EnumInAppEventData.filter(
  item => !item.onlyForEach && !(typeof item.hide === 'function' ? item.hide() : item.hide),
).map((item) => {
  const dummy = _.cloneDeep(item);
  dummy.children = item.children.slice(0, 3).filter(
    child => !child.onlyForEach && !(typeof child.hide === 'function' ? child.hide() : child.hide),
  );
  dummy.children.forEach((child, index) => {
    child.attribute = index + 1;
  });
  return dummy;
});

// https://bytedance.feishu.cn/wiki/wikcnlF7RQtO87J8xLfcyAQtVTb#
// 这个需求中，把自定义列分为 metrics（以前的列） 和 基本信息
// custom-list 基本信息模块 =》名称和ID列表
export const EnumSettingNamesIDs = [
  {
    i18nPath: 'common_custom_col_campaign_name',
    key: 'campaign_name',
    sortable: false,
    showLevel: ['adgroup', 'ad'], // 显示层级，只在 campaign , adgroup ,ad 哪几个层级展示
    useRootKey: true, // 使用data的根节点属性
    'min-width': COLUMN_WIDTH.XL,
  },
  {
    i18nPath: 'common_custom_col_campaign_id',
    key: 'campaign_id',
    sortable: false,
    showLevel: ['campaign', 'adgroup', 'ad'],
    useRootKey: true,
    'min-width': COLUMN_WIDTH.XL,
  },
  {
    i18nPath: 'common_custom_col_adgroup_name',
    key: 'ad_name',
    sortable: false,
    showLevel: ['ad'],
    useRootKey: true,
    'min-width': COLUMN_WIDTH.XL,
  },
  {
    i18nPath: 'common_ad_group_id',
    key: 'ad_id',
    sortable: false,
    showLevel: ['adgroup', 'ad'],
    useRootKey: true,
    'min-width': COLUMN_WIDTH.XL,
  },
  {
    i18nPath: 'common_diagnosis_suggestion',
    key: 'diagnosis_status',
    exportKeys: ['diagnosis_status', 'learn_phase_status', 'diagnosis_time'],
    sortable: true,
    useRootKey: true,
    showLevel: ['adgroup'],
    hide: () => !isDiagnosisV2White() || !store.getters['diagnosis/libraResultIsTrue'],
  },
  {
    i18nPath: 'list_creative_id',
    key: 'creative_id',
    sortable: false,
    showLevel: ['ad'],
    useRootKey: true,
    'min-width': COLUMN_WIDTH.XL,
  },
];
// custom-list 基本信息模块 =》状态和时间列表
export const EnumSettingStatusDates = [
  {
    i18nPath: 'common_custom_col_create_time',
    key: 'create_time',
    sortable: true,
    useRootKey: true,
    showLevel: ['campaign', 'adgroup', 'ad'],
  },
];

// custom-list 基本信息列 =》Objective & Budget & Goal
export const EnumSettingObjectiveBudgetGoal = [
  {
    i18nPath: 'campaign_objective',
    key: 'objective_type',
    sortable: false,
    useRootKey: true,
    showLevel: ['campaign', 'adgroup', 'ad'],
  },
  {
    // 暂时没有想到更好的办法
    // 因为 campgian 的 budget 和 adgroup budget key一样，会导致列表标识一样，报错
    // 因此这里 把 campaign 下的，key改名为 campaign_budget, 然后在渲染内容时，使用 formatter 再自定义成 budget
    i18nPath: 'common_custom_col_campaign_budget',
    key: 'campaign_budget',
    sortable: false,
    useRootKey: true,
    showLevel: ['campaign'],
  },
  {
    i18nPath: 'common_custom_col_adgroup_budget',
    key: 'budget',
    sortable: false,
    useRootKey: true,
    showLevel: ['adgroup'],
  },
  {
    i18nPath: 'common_custom_col_adgroup_ad_scheduling',
    key: 'schedule',
    sortable: false,
    useRootKey: true,
    showLevel: ['adgroup'],
  },
  {
    i18nPath: 'common_custom_col_bid',
    key: 'bid',
    sortable: false,
    useRootKey: true,
    showLevel: ['adgroup'],
  },
  {
    i18nPath: 'common_custom_col_adgroup_deep_cpa_bid',
    key: 'deep_cpa_bid',
    sortable: true,
    useRootKey: true,
    showLevel: ['adgroup'],
  },
  {
    i18nPath: 'common_custom_col_adgroup_aeo_type',
    key: 'aeo_type',
    useRootKey: true,
    showLevel: ['adgroup'],
  },
];
// custom-list 基本信息列 =》创意
export const EnumSettingAdCreative = [
  {
    i18nPath: 'ad_text',
    key: 'title',
    sortable: false,
    useRootKey: true,
    showLevel: ['ad'],
  },
];

// Other 的信息列配置
export const EnumSettingOther = [
  {
    i18nPath: 'module_po_number',
    key: 'po_number',
    sortable: false,
    useRootKey: true,
    showLevel: ['campaign'],
    hoverKey: 'module_po_number_tips',
    // 配置 learnMoreLink 会在表格header title 的 hover部分最后展示一个 learnMore 点击跳转到该链接
    learnMoreLink: '/help/article?aid=10006162',
    hide: () => store.getters['account/isSelfAccount'],
  },
  {
    i18nPath: 'module_search_ads_negative_keywords',
    key: 'block_words_num',
    sortable: false,
    useRootKey: true,
    'min-width': COLUMN_WIDTH.L,
    showLevel: ['campaign', 'adgroup'],
    hide: () => !isSearchAdsMtWhite(), // 是否隐藏Negative keywords自定义列
  },
];

// 自定义列总设置分类名称（竞价）
// 需求：https://bytedance.feishu.cn/wiki/wikcnlF7RQtO87J8xLfcyAQtVTb
export const CustomColSettings = [
  {
    title: 'custom_setting_names_ids',
    keyIndex: METRIC_TYPE.SETTINGS_NAMES_IDS,
  },
  {
    title: 'custom_setting_status_dates',
    keyIndex: METRIC_TYPE.SETTINGS_STATUS_DATES,
  },
  {
    title: 'custom_setting_objective_budget_goal',
    keyIndex: METRIC_TYPE.SETTINGS_OBJECTIVE_BUDGET_GOAL,
  },
  {
    title: 'custom_setting_ad_creative',
    keyIndex: METRIC_TYPE.SETTINGS_AD_CREATIVE,
  },
  {
    title: 'others',
    keyIndex: METRIC_TYPE.SETTINGS_OTHER,
  },
];

// 自定义列总分类名称（竞价）
export const CustomColMainCampaignSort = [
  {
    title: 'custom_basic_data',
    keyIndex: METRIC_TYPE.BASIC_DATA,
  },
  {
    title: 'custom_video_play',
    keyIndex: METRIC_TYPE.VIDEO_PLAY,
  },
  {
    title: 'custom_engagement',
    keyIndex: METRIC_TYPE.ENGAGEMENT,
  },
  {
    title: 'metric_interactive_add_on',
    keyIndex: METRIC_TYPE.ASSET,
  },
  {
    title: 'custom_live',
    keyIndex: METRIC_TYPE.LIVE,
  },
  {
    title: 'custom_app_event',
    keyIndex: METRIC_TYPE.APP_EVENT,
    items: [
      {
        title: 'custom_statistic_each',
        icon: ViPointGray,
        clickIcon: ViPointBlue,
      },
      {
        title: 'custom_statistic_unique',
        icon: ViPointGray,
        clickIcon: ViPointBlue,
      },
    ],
  },
  {
    title: 'custom_app_event_skan',
    keyIndex: METRIC_TYPE.SKAN_APP_EVENT,
    hide: () => !store.getters['dashboard/isDedicatedCampaignExist'],
  },
  {
    title: 'custom_attribution',
    keyIndex: METRIC_TYPE.ATTRIBUTION,
  },
  {
    // 页面事件数据
    title: 'custom_page_event',
    keyIndex: METRIC_TYPE.PAGE_EVENT,
  },
  {
    // Onsite 事件数据 https://bytedance.feishu.cn/docs/doccnhFDgrtonsfWDdRjbFMCs7f#
    title: 'custom_onsite_event',
    keyIndex: METRIC_TYPE.ONSITE_EVENT,
  },
];

// 汇总所有数据（竞价）
export const EnumAllStatsData = () => [
  ...EnumCommonData.map(item => ({
    ...item,
    keyIndex: METRIC_TYPE.BASIC_DATA,
  })),
  ...EnumConversionData.map(item => ({
    ...item,
    keyIndex: METRIC_TYPE.BASIC_DATA,
  })),
  ...EnumBasicSkanData.map(item => ({
    ...item,
    ...skanHoverRendered(item),
    keyIndex: METRIC_TYPE.BASIC_DATA,
  })),
  ...EnumInAppEventSkanData.map(item => ({
    ...item,
    children: item.children.map(childItem => ({
      ...childItem,
      ...skanHoverRendered(childItem),
    })),
    keyIndex: METRIC_TYPE.SKAN_APP_EVENT,
  })),
  ...EnumEngagementData.map(item => ({
    ...item,
    keyIndex: METRIC_TYPE.ENGAGEMENT,
  })),
  ...EnumAssetData.map(item => ({
    ...item,
    keyIndex: METRIC_TYPE.ASSET,
  })),
  ...EnumLiveData.map(item => ({
    ...item,
    keyIndex: METRIC_TYPE.LIVE,
  })),
  ...EnumInAppEventData.map(item => ({
    ...item,
    children: inAppEventDcHover(item.children),
    keyIndex: METRIC_TYPE.APP_EVENT,
  })),
  ...EnumAttributionData.map((item) => {
    let rendered = {
      ...item,
      keyIndex: METRIC_TYPE.ATTRIBUTION,
    };
    if (EnumAttributionSkanData.map(metric => metric.key).includes(item.key)) {
      rendered = {
        ...rendered,
        ...skanHoverRendered(item),
      };
    }
    return rendered;
  }),
  ...EnumVideoPlayData.map(item => ({
    ...item,
    keyIndex: METRIC_TYPE.VIDEO_PLAY,
  })),
  ...EnumInAppEventDataEach.map(item => ({
    ...item,
    children: inAppEventDcHover(item.children),
    keyIndex: METRIC_TYPE.APP_EVENT_EACH,
  })),
  ...EnumInAppEventDataUnique.map(item => ({
    ...item,
    children: inAppEventDcHover(item.children),
    keyIndex: METRIC_TYPE.APP_EVENT_UNIQUE,
  })),
  ...(isAdmin() ? EnumUVData : []).map(item => ({
    ...item,
    keyIndex: METRIC_TYPE.VIDEO_PLAY,
  })),
  ...EnumPageEventData.map(item => ({
    ...item,
    children: pageEventHover(item.children),
    keyIndex: METRIC_TYPE.PAGE_EVENT,
  })),
  ...EnumOnsiteEventData.map(item => ({
    ...item,
    keyIndex: METRIC_TYPE.ONSITE_EVENT,
  })),
  ...EnumSettingNamesIDs.map(item => ({
    ...item,
    keyIndex: METRIC_TYPE.SETTINGS_NAMES_IDS,
  })),
  ...EnumSettingStatusDates.map(item => ({
    ...item,
    keyIndex: METRIC_TYPE.SETTINGS_STATUS_DATES,
  })),
  ...EnumSettingObjectiveBudgetGoal.map(item => ({
    ...item,
    keyIndex: METRIC_TYPE.SETTINGS_OBJECTIVE_BUDGET_GOAL,
  })),
  ...EnumSettingAdCreative.map(item => ({
    ...item,
    keyIndex: METRIC_TYPE.SETTINGS_AD_CREATIVE,
  })),
  ...EnumSettingOther.map(item => ({
    ...item,
    keyIndex: METRIC_TYPE.SETTINGS_OTHER,
  })),
].filter(item => !(typeof item.hide === 'function' ? item.hide() : item.hide));
// ].filter(item => !item.hide);

// hover到该字段是否显示提示文案
// 基本信息列，不展示hover，因为就是一些基础字段列，没有什么好解释的
export const isShowHoverTipByKeyIndex = keyIndex => ![
  METRIC_TYPE.SETTINGS_NAMES_IDS,
  METRIC_TYPE.SETTINGS_OBJECTIVE_BUDGET_GOAL,
  METRIC_TYPE.SETTINGS_STATUS_DATES,
  METRIC_TYPE.SETTINGS_AD_CREATIVE,
  // METRIC_TYPE.SETTINGS_OTHER, // PO Number 需要 展示hover信息
].includes(keyIndex);
// 是否展示negative keywords自定义列的hover信息
export const isShowHoverNkByKey = key => ![
  'block_words_num',
].includes(key);
